import React, { useEffect, useState } from "react";
import Card from "./_Card";
import Stepper from "../../../components/profile/Stepper";
import Button from "../../../components/ui/Button";
import PrefixInput from "../../../components/ui/PrefixInput";
import ProfileSidebar from "../../../components/reusable components/ProfileSidebar";
import { getProfileSocial, putProfileSocial, SocialProfileResponse } from "../../../api/profile";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const KNOWN = ["twitter", "x", "linkedin", "facebook", "meta", "instagram"] as const;

type SocialForm = {
  twitter: string;
  linkedin: string;
  facebook: string;
  instagram: string;
};

type ExtraSocial = { name: string; value: string };

const normalizeKey = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const fromApi = (api: SocialProfileResponse) => {
  const sm = api?.social_media || {};
  const form: SocialForm = {
    twitter: (sm.twitter ?? sm.x ?? "") || "",
    linkedin: sm.linkedin ?? "",
    facebook: (sm.facebook ?? sm.meta ?? "") || "",
    instagram: sm.instagram ?? "",
  };

  const extras: ExtraSocial[] = [];
  Object.entries(sm).forEach(([k, v]) => {
    const key = k.toLowerCase();
    if (!KNOWN.includes(key as any)) {
      extras.push({ name: k, value: String(v ?? "") });
    }
  });

  return { form, extras };
};

export default function Social() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [justCompleted, setJustCompleted] = useState(false);

  const [form, setForm] = useState<SocialForm>({
    twitter: "",
    linkedin: "",
    facebook: "",
    instagram: "",
  });
  const [extras, setExtras] = useState<ExtraSocial[]>([]);

  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProfileSocial();
      const { form, extras } = fromApi(data);
      setForm(form);
      setExtras(extras);
    } catch (e: any) {
      const msg = e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.social_media ||
        e?.response?.data?.detail|| "Failed to load social profiles";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      await load();
    })();
    return () => {
      active = false;
    };
  }, []);

  const buildPayload = (xtras = extras) => {
    const social_media: Record<string, string> = {
      twitter: form.twitter || "",
      linkedin: form.linkedin || "",
      facebook: form.facebook || "",
      instagram: form.instagram || "",
    };
    xtras.forEach(({ name, value }) => {
      const key = normalizeKey(name) || name;
      social_media[key] = value || "";
    });
    return { social_media };
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await putProfileSocial(buildPayload());
      toast.success("Social profiles updated");
      await load();

      setJustCompleted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => navigate("/dashboard"), 700);
    } catch (e: any) {
      const msg = e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.social_media ||
        e?.response?.data?.detail|| "Failed to update social profiles";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOpen = () => {
    setNewName("");
    setNewValue("");
    setOpen(true);
  };

  const handleAddSave = async () => {
    const key = normalizeKey(newName);
    if (!key) {
      toast.error("Enter a social media name");
      return;
    }
    const existingKeys = new Set<string>([
      "twitter",
      "linkedin",
      "facebook",
      "instagram",
      ...extras.map((e) => normalizeKey(e.name)),
    ]);
    if (existingKeys.has(key)) {
      toast.error("That social media is already added");
      return;
    }

    try {
      setSaving(true);
      const nextExtras = [...extras, { name: newName, value: newValue }];
      await putProfileSocial(buildPayload(nextExtras));
      toast.success("Social platform added");
      setOpen(false);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.social_media ||
        e?.response?.data?.detail|| "Failed to add social platform";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen gap-5 px-3">
      <ProfileSidebar isProfileRoute={true} />
      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="h-full">
          <Card>
            <div className="px-6 py-5">
              <Stepper active={justCompleted ? "done" : "social"} />
            </div>

            {loading && <div className="px-6 -mt-2 pb-2 text-sm text-[#6b7280]">Loading social profiles…</div>}
            {error && <div className="px-6 -mt-2 pb-2 text-sm text-red-600">{error}</div>}

            <div className="px-6 pb-6">
              <div className="flex justify-between items-center mt-2 mb-4">
                <h3 className="text-[20px] font-semibold">Social profiles</h3>
                <button
                  type="button"
                  onClick={handleAddOpen}
                  className="h-9 px-3 rounded-lg bg-[#8CC38F] text-[#0F172A] text-sm font-medium inline-flex items-center gap-2"
                >
                  <span className="material-icons text-[18px] leading-none">add</span>
                  Add more
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-20">
                <div className="space-y-5">
                  <div>
                    <div className="text-sm font-semibold mb-2">X</div>
                    <PrefixInput
                      prefix="twitter.com/"
                      placeholder="company"
                      value={form.twitter}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setForm((s) => ({ ...s, twitter: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-2">LinkedIn</div>
                    <PrefixInput
                      prefix="linkedin.com/"
                      placeholder="company/"
                      value={form.linkedin}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setForm((s) => ({ ...s, linkedin: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <div className="text-sm font-semibold mb-2">Meta</div>
                    <PrefixInput
                      prefix="facebook.com/"
                      placeholder="company"
                      value={form.facebook}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setForm((s) => ({ ...s, facebook: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-2">Instagram</div>
                    <PrefixInput
                      prefix="instagram.com/"
                      placeholder="company"
                      value={form.instagram}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setForm((s) => ({ ...s, instagram: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Dynamic extras */}
              {extras.length > 0 && (
                <>
                  <div className="mt-8 text-sm font-semibold">More profiles</div>
                  <div className="grid md:grid-cols-2 gap-20 mt-3">
                    {extras.map((ex, idx) => (
                      <div key={`${ex.name}-${idx}`}>
                        <div className="text-xs text-[#6b7280] mb-2 capitalize">{ex.name}</div>
                        <PrefixInput
                          prefix={`${normalizeKey(ex.name)}.com/`}
                          placeholder="company"
                          value={ex.value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setExtras((arr) => {
                              const next = [...arr];
                              next[idx] = { ...next[idx], value: e.target.value };
                              return next;
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-8 flex justify-end gap-3">
                {/* <button type="button" className="h-10 px-4 rounded-[10px] border border-primary bg-white text-sm"> */}
                  {/* Skip */}
                {/* </button> */}
                <Button className="min-w-[96px]" disabled={saving} onClick={handleSubmit}>
                  {saving ? "Submitting…" : "Submit"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center">
          <div className="w-[min(520px,92vw)] rounded-xl bg-white shadow-lg border p-5">
            <div className="text-lg font-semibold mb-4">Add Social Platform</div>
            <div className="space-y-4">
              <div>
                <div className="text-sm mb-1">Social Media Name</div>
                <input
                  className="w-full h-10 rounded-lg border px-3 outline-none"
                  placeholder="e.g. YouTube"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Key will be <code>{normalizeKey(newName) || "…"}</code>
                </div>
              </div>
              <div>
                <div className="text-sm mb-1">Label / Handle</div>
                <input
                  className="w-full h-10 rounded-lg border px-3 outline-none"
                  placeholder="e.g. company"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="h-9 px-3 rounded-lg border"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <Button className="min-w-[96px]" onClick={handleAddSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
